import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './useAuth';
import LoadingContainer from '../../components/common/LoadingContainer';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'USER' | 'MANAGER' | 'ADMIN';
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole 
}) => {
  const { user, loading, userRole } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (loading) {
    return <LoadingContainer loading={loading} fullPage={true} message="AUTHENTICATING" />;
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access if requiredRole is specified
  if (requiredRole && userRole !== requiredRole) {
    // If user doesn't have the required role, navigate to dashboard
    if (userRole === 'ADMIN') {
      // Admins can access everything, so this shouldn't happen
      return <>{children}</>;
    } else if (userRole === 'MANAGER' && requiredRole === 'USER') {
      // Managers can access USER routes
      return <>{children}</>;
    } else {
      // Otherwise, redirect to dashboard
      return <Navigate to="/dashboard" replace />;
    }
  }

  // If authenticated and has the required role (or no required role), render children
  return <>{children}</>;
};

export default ProtectedRoute;
